import { BoxContainer, Input, PrimaryButton, ReactIcons, Switch } from "@kalyzee/kalyzee-ui";

import workBoxStyles from "../WorkBox.module.css";

import styles from "./SurveyWorkBox.module.css";

import { MeetingContainerAPI, useMeetingSelector } from "@kalyzee/kast-web-sdk";
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSdkContext } from "../../../contexts/sdk/context";
import { Card } from "../../common/Card";
import { Grid } from "../../common/Grid";
import clsx from "clsx";
import { toastSuccess } from "../../../helpers/toast";
import { SurveyIcons } from "./SurveyIcons";

const generateChoiceName = (idx: number) => {
  let name = '';
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const base = letters.length;

  while (idx >= 0) {
    name = letters[idx % base] + name;
    idx = Math.floor(idx / base) - 1;
  }

  return name;
};

const generateChoicesNames = (numChoices: number) => {
  return Array.from({ length: numChoices }, (_, idx) => generateChoiceName(idx));
}

enum SurveyType {
  YesNo = "YesNo",
  TrueFalse = "TrueFalse",
  ForAgainstNoOpinion = "ForAgainstNoOpinion",
  MultipleChoice = "MultipleChoice",
  Stars = "Stars",
  Custom = "Custom",
}

interface SurveyData {
  type: SurveyType;
  name: string;
  icon: React.ReactNode;
  question: {
    content: string;
    minChoiceNumber?: number;
    maxChoiceNumber?: number;
    choices: { content: string }[] | number;
  };
  color: string;
  backgroundColor: string;
}

const SurveyKinds: SurveyData[] = [
  {
    type: SurveyType.YesNo,
    name: "Oui / Non",
    icon: <SurveyIcons.YesNo />,
    question: {
      content: "Sondage Oui / Non",
      choices: [{ content: "Yes" }, { content: "No" }],
    },
    color: "#688EA4",
    backgroundColor: "#D0EEFF",
  },
 {
    type: SurveyType.TrueFalse,
    name: "Vrai / Faux",
    icon: <SurveyIcons.TrueFalse />,
    question: {
      content: "Sondage Vrai / Faux",
      choices: [{ content: "Vrai" }, { content: "Faux" }],
    },
    color: "#86A6FF",
    backgroundColor: "#E4EAFF",
  },
  {
    type: SurveyType.ForAgainstNoOpinion,
    name: "Pour / Contre",
    icon: <SurveyIcons.ForAgainstNoOpinion />,
    question: {
      content: "Sondage Pour / Contre",
      choices: [{ content: "Pour" }, { content: "Contre" }],
    },
    color: "#6667FF",
    backgroundColor: "#CCCEFF",
  },
  {
    type: SurveyType.MultipleChoice,
    name: "Choix multiples",
    icon: <SurveyIcons.MultipleChoice />,
    question: {
      content: "Sondage à choix multiples",
      choices: 3,
    },
    color: "#6DD4B2",
    backgroundColor: "#DAF5ED",
  },
  {
    type: SurveyType.Stars,
    name: "Étoiles",
    icon: <SurveyIcons.Stars />,
    question: {
      content: "Sondage Étoiles",
      choices: [
        { content: "0.5" },
        { content: "1" },
        { content: "1.5" },
        { content: "2" },
        { content: "2.5" },
        { content: "3" },
        { content: "3.5" },
        { content: "4" },
        { content: "4.5" },
        { content: "5" },
      ],
    },
    color: "#E143D9",
    backgroundColor: "#FBE6FA",
  },
  {
    type: SurveyType.Custom,
    name: "Customisé",
    icon: <SurveyIcons.Custom />,
    question: {
      content: "Sondage à choix multiples",
      choices: [
        { content: "" },
        { content: "" },
        { content: "" },
      ],
    },
    color: "#6DD4B2",
    backgroundColor: "#DAF5ED",
  },
];

const SurveyCardIcon = React.memo(({ 
  survey,
  selected,
  setData,
}: { 
  survey: SurveyData; 
  selected: boolean;
  setData: (data: SurveyData) => void; 
}) => {
  const onClick = useCallback(() => {
    setData(survey);
  }, [survey, setData]);
  return (
    <Card.Icon 
      icon={survey.icon}
      text={survey.name}
      selected={selected}
      onClick={onClick}
    />
  )
});

const CreatorFirstStep = ({
  data,
  setData,
}: {
  data: SurveyData;
  setData: (data: SurveyData) => void;
}) => {
  return (
    <Grid
      ratio={1}
      elements={
        SurveyKinds.map((survey) => (
          <SurveyCardIcon
            survey={survey}
            selected={data.type === survey.type}
            setData={setData}
          />
        ))
      }
    />
  );
};

const CreatorSecondStep = ({
  data,
  setData,
}: {
  data: SurveyData;
  setData: (data: SurveyData) => void;
}) => {
  const lastChoiceRef = useRef<HTMLDivElement>(null);

  const choices = data.question.choices;

  useLayoutEffect(() => {
    lastChoiceRef.current?.scrollIntoView({ block: "end", behavior: "auto" });
  }, [data.question.choices instanceof Array ? data.question.choices.length : null]);

  const onAddChoice = () => {
    if (!(choices instanceof Array)) return;
    if (choices.length >= 20) return;
    const newChoices = [...choices];
    if (newChoices.length < 20) {
      newChoices.push({ content: "" });
    }
    setData({
      ...data,
      question: {
        ...data.question,
        choices: newChoices,
        maxChoiceNumber: data.question.maxChoiceNumber ? newChoices.length : undefined,
      },
    });
  };

  const onRemoveChoice = () => {
    if (!(choices instanceof Array)) return;
    const newChoices = [...choices];
    newChoices.pop();
    setData({
      ...data,
      question: {
        ...data.question,
        choices: newChoices,
        maxChoiceNumber: data.question.maxChoiceNumber ? newChoices.length : undefined,
      },
    });
  };
  
  const onSwitchMultipleChoiceNumber = (value: boolean) => {
    setData({
      ...data,
      question: {
        ...data.question,
        maxChoiceNumber: value ? choices instanceof Array ? choices.length : choices : undefined,
      },
    });
  };

  const renderMultipleChoicesSwitch = () => {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "1em" }}>
        <Switch 
          value={!!data.question.maxChoiceNumber}
          onChange={onSwitchMultipleChoiceNumber}
          nativeProps={{
            tabIndex: 0,
            onKeyDown: (e) => {
              if (e.key === "Enter") {
                onSwitchMultipleChoiceNumber(!data.question.maxChoiceNumber);
              }
            }
          }}
        />
        <div>Plusieurs choix possibles</div>
      </div>
    );
  };
  
  const renderCustomChoices = () => {
    if (!(choices instanceof Array)) return null;
    if (!choices.length) {
      return (
        <div style={{ 
          width: "100%", 
          height: "100%", 
          border: "3px dashed var(--color-element-workbox-survey-text-1)", 
          borderRadius: "1em", 
          display: "flex", 
          justifyContent: "center", 
          alignItems: "center",
          color: "var(--color-element-workbox-survey-text-1)",
        }}>
          J'ajoute des réponses !
        </div>
      );
    }
    return (
      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: "1em", overflowY: "auto" }}>
        {choices.map((choice, idx) => (
          <div key={idx} ref={idx === choices.length - 1 ? lastChoiceRef : undefined}>
          <Input 
            style={{ 
              width: "100%",
              padding: "0.5em 1em",
              border: "3px dashed var(--color-element-workbox-survey-text-1)", 
              borderRadius: "0.5em",
              fontSize: "1.2em",
              backgroundColor: "transparent",
            }}
            nativeProps={{
              value: choice.content,
              placeholder: generateChoiceName(idx),
              onChange: (e) => {
                const newChoices = [...choices];
                const newValue = e.currentTarget.value;
                const isEmpty = newValue.trim().length === 0;
                newChoices[idx] = { content: isEmpty ? "" : newValue }; // only trim if empty, allows to have spaces
                setData({
                  ...data,
                  question: {
                    ...data.question,
                    choices: newChoices,
                  },
                });
              },
            }}
            item={
              <PrimaryButton onClick={() => {
                const newChoices = [...choices];
                newChoices.splice(idx, 1);
                setData({
                  ...data,
                  question: {
                    ...data.question,
                    choices: newChoices,
                  },
                });
              }}>
                <ReactIcons.Trash style={{ height: "1.5em", color: "var(--color-red-1)" }} />
              </PrimaryButton>
            }
          />
          </div>
        ))}
      </div>
    );
  };

  if (data.type === SurveyType.MultipleChoice) {
    const currChoices = data.question.choices as number;
    return (
      <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: "1em" }}>
        <div className={styles.contentTitle}>Nombre de choix à proposer :</div>
        <Grid
          ratio={1}
          style={{ flex: 1, minHeight: 0 }}
          elements={
            Array.from({ length: 4 }).map((_, idx) => {
              const choices = idx + 3;
              return (
                <Card.Radio
                  text={`${choices} choix`}
                  subtext={generateChoicesNames(choices).join(', ')}
                  checked={currChoices === choices}
                  onClick={() => {
                    setData({
                      ...data,
                      question: {
                        ...data.question,
                        choices,
                        maxChoiceNumber: data.question.maxChoiceNumber ? choices : undefined,
                      },
                    });
                  }}
                />
              );
            })
          }
        />
        {renderMultipleChoicesSwitch()}
      </div>
    );
  }

  if (data.type === SurveyType.Custom) {
    if (!(choices instanceof Array)) return null;
     return (
      <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: "1em" }}>
        <div className={styles.contentTitle}>QCM Customisé</div>
        <div style={{ marginTop: "1em", flex: 1, minHeight: 0, width: "80%", display: "flex", flexDirection: "column", alignItems: "center", gap: "1em" }}>
          <div className={styles.inputContainer}>
            <div className={styles.textColor0}>Énoncé de la question :</div>
            <Input 
              className={styles.input} 
              nativeProps={{
                autoFocus: true,
                value: data.question.content,
                onChange: (e) => {
                  setData({
                    ...data,
                    question: {
                      ...data.question,
                      content: e.currentTarget.value,
                    },
                  });
                },
                placeholder: "6 x 7 = ?",
              }}  
            />
          </div>
          <div style={{ display: "flex", gap: "1em", fontSize: "1em", fontWeight: 600 }}>
            <PrimaryButton 
              text="-" 
              className={clsx({
                [styles.answerQuantityBtn]: true,
                [styles.disabledBtn]: choices.length <= 0,
              })}  
              onClick={onRemoveChoice} 
            />
            <PrimaryButton 
              text="+" 
              className={clsx({
                [styles.answerQuantityBtn]: true,
                [styles.disabledBtn]: choices.length >= 20,
              })} 
              onClick={onAddChoice} 
            />
          </div>
          <div style={{ flex: 1, minHeight: 0, width: "100%", overflowY: "auto" }}>
            {renderCustomChoices()}
          </div>
          {renderMultipleChoicesSwitch()}
        </div>
      </div>
    );
  }

  return null;
}

export const SurveyCreatorWorkBox = ({
  meetingAPI,
  onClose,
}: {
  meetingAPI: MeetingContainerAPI;
  onClose?: () => void;
}) => {
  const [step, setStep] = useState(0);
  const [data, setData] = useState<SurveyData>(SurveyKinds[0]);

  const sdkCtx = useSdkContext();
  const [meetingId] = useMeetingSelector(meetingAPI, "meeting", meeting => meeting?.id);

  const canCreate = useMemo(() => {
    return data.question.content.trim().length > 0 && (
      data.question.choices instanceof Array ? data.question.choices.length >= 1 : true
    );
  }, [data]);

  const onCreate = async (data: SurveyData) => {
    const client = sdkCtx.client.getWebsocketClient();
    if (!client || !meetingId) return;
    const result = await client.emitter.survey.create({
      meetingId,
      name: data.name,
      updateAnswers: true,
      questions: [{
        ...data.question,
        choices: typeof data.question.choices === "number"
          ? generateChoicesNames(data.question.choices).map(choiceName => ({ content: choiceName }))
          : data.question.choices.map((choice, idx) => {
            const cleanedContent = choice.content.trim();
            return ({ content: cleanedContent || generateChoiceName(idx) })
          }),
      }]
    });
    if (result?.error || !result?.response) {
      console.error("Error creating survey: ", result?.error);
      return;
    }
    toastSuccess("Sondage lancé");
  };

  const goToNextStep = () => {
    if (step === 0 && (data.type === SurveyType.MultipleChoice || data.type === SurveyType.Custom)) {
      setStep(1);
    } else {
      onCreate?.(data);
    }
  };

  const renderStep = () => {
    if (step === 0) {
      return <CreatorFirstStep data={data} setData={setData} />;
    } else if (step === 1) {
      return <CreatorSecondStep data={data} setData={setData} />;
    }
    return null;
  };

  const renderContent = () => (
    <>
      <div style={{ flex: 1, minHeight: 0, display: "flex", justifyContent: "center", width: "100%" }}>
        {renderStep()}
      </div>
      <PrimaryButton
        className={clsx({
          [styles.validateButton]: true,
          [styles.disabledBtn]: step === 1 && !canCreate,
        })}
        text="Valider"
        onClick={goToNextStep}
      />
    </>
  );

  return (
    <BoxContainer
      className={styles.container}
      topBarProps={{
        left: step > 0 ? [
          <div 
            style={{ display: "flex", alignItems: "center", gap: "0.5em", cursor: "pointer" }} 
            onClick={() => setStep(step - 1)}
          >
            <ReactIcons.ArrowBack />
            <span>{data.name}</span>
          </div>
        ]: [
          <ReactIcons.Stats style={{ height: "1.5em", width: "1.5em" }} />,
        ],
        center: [
          <span className={workBoxStyles.label}>Créer un sondage</span>
        ],
        right: onClose ? [
          <PrimaryButton onClick={onClose}>
            <ReactIcons.Close style={{ height: "0.8em" }} />
          </PrimaryButton>
        ] : [],
      }}
    >
      <div className={styles.content}>
        {renderContent()}
      </div>
    </BoxContainer>
  );
};
